// NutritionFactsPanel.js
// Full FDA-style Nutrition Facts label display + PDF export, plus a Canadian
// Nutrition Facts table / Valeur nutritive export (see CanadianNutritionLabel.js).
// Used in AdminProductPage inside the Manage Ingredients modal (🧪 Nutrition tab).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import NutritionCertificateButton from './NutritionCertificate';
import CanadianNutritionLabel from './CanadianNutritionLabel';
import {
  getProductNutritionProfile,
  calculateNutrition,
  saveProductNutritionProfile,
  getIngredientCoverage,
  searchFdc,
  getIngredientNutrition,
  saveIngredientNutrition,
  importFromFdc,
  deriveFatProfile,
  getNutritionVersions,
  approveNutritionVersion,
  getNutritionReadiness,
  checkNutritionPlausibility,
} from '../services/nutritionService';
import ConfirmModal from './ConfirmModal';

// ─────────────────────────────────────────────────────────────────────────────
// FDA RACC — Reference Amounts Customarily Consumed (21 CFR 101.12, Table 2)
// { category, racc (g), note, keywords[] }
// ─────────────────────────────────────────────────────────────────────────────
const FDA_RACC = [
  // Bakery
  { category: 'Bagels',                     racc: 55,  note: '1 mini bagel or ½ regular',    keywords: ['bagel'] },
  { category: 'Biscuits',                   racc: 55,  note: '1 biscuit',                    keywords: ['biscuit'] },
  { category: 'Bread, loaf (sliced)',        racc: 30,  note: '1 regular slice (~1 oz)',       keywords: ['bread', 'loaf', 'white bread', 'wheat bread', 'sourdough', 'sandwich'] },
  { category: 'Bread, quick (banana, etc.)', racc: 55,  note: '1 slice',                      keywords: ['quick bread', 'banana bread', 'zucchini bread', 'pumpkin bread'] },
  { category: 'Brownies',                   racc: 40,  note: '1 brownie (~1.5 oz)',           keywords: ['brownie'] },
  { category: 'Cake, plain / without icing',racc: 125, note: '1 piece',                      keywords: ['cake', 'pound cake', 'sponge cake'] },
  { category: 'Cake, with icing / filling', racc: 125, note: '1 piece',                      keywords: ['layer cake', 'frosted cake', 'cupcake'] },
  { category: 'Coffee cake / crumb cake',   racc: 55,  note: '1 piece',                      keywords: ['coffee cake', 'crumb cake', 'streusel'] },
  { category: 'Cookies',                    racc: 30,  note: 'approx. 2–3 medium cookies',   keywords: ['cookie', 'biscotto', 'biscotti', 'shortbread'] },
  { category: 'Crackers, plain',            racc: 15,  note: 'approx. 5–7 crackers',         keywords: ['cracker', 'saltine', 'graham cracker'] },
  { category: 'Crackers, with filling',     racc: 30,  note: 'approx. 2–4 sandwich crackers',keywords: ['cracker sandwich', 'cheese cracker'] },
  { category: 'Croissants',                 racc: 55,  note: '1 medium croissant',           keywords: ['croissant', 'crescent'] },
  { category: 'Doughnuts / donuts',         racc: 55,  note: '1 medium doughnut',            keywords: ['donut', 'doughnut'] },
  { category: 'English muffins',            racc: 55,  note: '1 muffin',                     keywords: ['english muffin', 'muffin tin'] },
  { category: 'Focaccia / flatbread',       racc: 55,  note: '1 piece (~2 oz)',              keywords: ['focaccia', 'flatbread', 'fougasse'] },
  { category: 'French toast',               racc: 45,  note: '1 slice',                      keywords: ['french toast'] },
  { category: 'Muffins, sweet',             racc: 55,  note: '1 muffin',                     keywords: ['muffin', 'blueberry muffin', 'bran muffin'] },
  { category: 'Pancakes',                   racc: 55,  note: '3 medium pancakes',            keywords: ['pancake', 'flapjack'] },
  { category: 'Pies, fruit / custard',      racc: 125, note: '1 slice (⅛ of 8" pie)',        keywords: ['pie', 'tart', 'fruit pie', 'custard pie', 'cream pie'] },
  { category: 'Pita bread',                 racc: 55,  note: '½ pita or 1 mini pita',        keywords: ['pita', 'pita bread'] },
  { category: 'Pizza (regular crust)',       racc: 140, note: '1 slice',                      keywords: ['pizza', 'thin crust', 'pan pizza', 'deep dish'] },
  { category: 'Pretzels',                   racc: 30,  note: 'approx. 5 small / 1 large',   keywords: ['pretzel'] },
  { category: 'Rolls, dinner',              racc: 28,  note: '1 small roll',                 keywords: ['dinner roll', 'small roll', 'parker house'] },
  { category: 'Rolls, hamburger / hotdog',  racc: 43,  note: '1 bun',                        keywords: ['bun', 'hamburger bun', 'hotdog bun', 'brioche bun'] },
  { category: 'Scones',                     racc: 55,  note: '1 scone',                      keywords: ['scone'] },
  { category: 'Toaster pastries',           racc: 54,  note: '1 pastry',                     keywords: ['toaster pastry', 'pop tart', 'strudel'] },
  { category: 'Tortillas, corn',            racc: 30,  note: '2 small tortillas',            keywords: ['corn tortilla', 'tortilla chip'] },
  { category: 'Tortillas, flour',           racc: 55,  note: '1 medium tortilla',            keywords: ['flour tortilla', 'wrap', 'burrito tortilla'] },
  { category: 'Waffles',                    racc: 35,  note: '1 waffle',                     keywords: ['waffle'] },
  // Pasta & grains
  { category: 'Pasta, dry',                 racc: 55,  note: 'dry weight, ~2 oz',            keywords: ['pasta', 'spaghetti', 'penne', 'noodle', 'fettuccine', 'linguine'] },
  { category: 'Pasta, cooked',              racc: 140, note: 'cooked weight, ~½ cup',        keywords: ['cooked pasta', 'cooked noodle'] },
  { category: 'Rice, dry',                  racc: 45,  note: 'dry weight, ~¼ cup',           keywords: ['rice', 'dry rice'] },
  { category: 'Rice, cooked',               racc: 140, note: 'cooked weight, ~½ cup',        keywords: ['cooked rice', 'steamed rice'] },
  // Cereals
  { category: 'Cereals, ready-to-eat (flakes/puffs)', racc: 30, note: '~1 cup',             keywords: ['cereal', 'granola', 'corn flakes', 'puffed cereal'] },
  { category: 'Cereals, ready-to-eat (dense/high fiber)', racc: 55, note: '~½ cup',         keywords: ['bran cereal', 'muesli', 'oat cluster'] },
  { category: 'Oatmeal / hot cereal, dry',  racc: 40,  note: 'dry weight',                  keywords: ['oatmeal', 'oats', 'hot cereal', 'porridge', 'grits'] },
  // Snacks
  { category: 'Chips (potato, tortilla)',   racc: 30,  note: 'approx. 1 oz',                keywords: ['chip', 'chips', 'crisps', 'potato chip'] },
  { category: 'Popcorn',                    racc: 30,  note: 'approx. 3 cups popped',       keywords: ['popcorn'] },
  { category: 'Granola bars',               racc: 40,  note: '1 bar',                        keywords: ['granola bar', 'energy bar', 'cereal bar'] },
  // Dairy & frozen desserts
  { category: 'Milk, all types',            racc: 240, note: '1 cup',                        keywords: ['milk', 'whole milk', 'almond milk', 'oat milk', 'soy milk', 'buttermilk'] },
  { category: 'Cream, half & half',         racc: 15,  note: '1 tbsp',                        keywords: ['cream', 'half and half', 'coffee creamer', 'heavy cream'] },
  { category: 'Sour cream',                 racc: 30,  note: '2 tbsp',                        keywords: ['sour cream', 'crema'] },
  { category: 'Yogurt',                     racc: 170, note: '~6 oz container',              keywords: ['yogurt', 'greek yogurt', 'kefir', 'skyr'] },
  { category: 'Cheese, natural',            racc: 30,  note: '1 oz',                          keywords: ['cheese', 'cheddar', 'mozzarella', 'gouda', 'provolone', 'string cheese', 'parmesan'] },
  { category: 'Cheese, cottage/ricotta',    racc: 110, note: '~½ cup',                        keywords: ['cottage cheese', 'ricotta'] },
  { category: 'Cream cheese',               racc: 30,  note: '2 tbsp',                        keywords: ['cream cheese', 'mascarpone'] },
  {
    category: 'Ice cream / frozen dairy dessert', racc: 100,
    note: '2/3 cup — gram weight varies by density/overrun; weigh 2/3 cup of your own finished product rather than using this figure as-is',
    keywords: ['ice cream', 'gelato', 'frozen yogurt', 'frozen custard', 'sorbet', 'sherbet', 'frozen dessert', 'soft serve'],
  },
  {
    category: 'Ice cream novelties (bars, sandwiches, cones)', racc: 60,
    note: '1 unit — labeled per piece, not RACC-derived; use your actual finished-unit weight',
    keywords: ['ice cream bar', 'ice cream sandwich', 'ice cream cone', 'popsicle', 'novelty', 'paleta'],
  },
  // Eggs
  { category: 'Eggs',                       racc: 50,  note: '1 large egg',                   keywords: ['egg', 'eggs', 'liquid egg'] },
  // Fats, oils & dressings
  { category: 'Butter / margarine',         racc: 14,  note: '1 tbsp',                        keywords: ['butter', 'margarine', 'ghee'] },
  { category: 'Cooking oil',                racc: 14,  note: '1 tbsp',                        keywords: ['oil', 'olive oil', 'vegetable oil', 'canola oil'] },
  { category: 'Salad dressing / mayonnaise',racc: 30,  note: '2 tbsp (~1 oz)',                keywords: ['dressing', 'mayo', 'mayonnaise', 'vinaigrette', 'salad dressing'] },
  // Fruits & fruit products
  { category: 'Fresh fruit, whole',         racc: 140, note: 'approx. 1 medium piece',        keywords: ['apple', 'orange', 'banana', 'fresh fruit', 'pear', 'peach', 'plum'] },
  { category: 'Dried fruit',                racc: 40,  note: 'approx. ¼ cup',                 keywords: ['dried fruit', 'raisin', 'dried apricot', 'dried mango', 'craisin'] },
  { category: 'Fruit juice',                racc: 240, note: '1 cup (8 fl oz)',               keywords: ['juice', 'fruit juice', 'apple juice', 'orange juice'] },
  { category: 'Jam, jelly, preserves',      racc: 15,  note: '1 tbsp',                        keywords: ['jam', 'jelly', 'preserves', 'marmalade', 'fruit spread'] },
  // Vegetables & sauces
  { category: 'Vegetables, raw or cooked',  racc: 85,  note: 'approx. ½–1 cup',               keywords: ['vegetable', 'broccoli', 'carrot', 'spinach', 'lettuce', 'salad greens'] },
  { category: 'Tomato / pasta sauce',       racc: 125, note: '~½ cup',                        keywords: ['tomato sauce', 'marinara', 'pasta sauce', 'pizza sauce'] },
  // Meat, poultry & seafood
  { category: 'Meat, poultry, seafood (entrée cut)', racc: 85, note: '3 oz cooked',           keywords: ['chicken', 'beef', 'pork', 'meat', 'poultry', 'steak', 'ground beef', 'turkey'] },
  { category: 'Fish / seafood',             racc: 85,  note: '3 oz cooked',                   keywords: ['fish', 'salmon', 'shrimp', 'tuna', 'seafood', 'crab', 'lobster'] },
  { category: 'Deli meat / cold cuts',      racc: 55,  note: '~2 oz',                         keywords: ['deli meat', 'cold cuts', 'ham', 'salami', 'pepperoni', 'bacon', 'sausage'] },
  // Mixed dishes & soups
  { category: 'Soup',                       racc: 245, note: '1 cup',                         keywords: ['soup', 'broth', 'bisque', 'chowder'] },
  { category: 'Prepared entrée / frozen meal', racc: 340, note: 'typical single-serve meal',  keywords: ['entree', 'entrée', 'frozen meal', 'casserole', 'lasagna', 'tv dinner'] },
  // Nuts, seeds & spreads
  { category: 'Nuts, seeds (snack)',        racc: 30,  note: '1 oz',                          keywords: ['nuts', 'almond', 'peanut', 'cashew', 'walnut', 'pistachio', 'seeds', 'sunflower seeds'] },
  { category: 'Nut butter',                 racc: 32,  note: '2 tbsp',                        keywords: ['peanut butter', 'almond butter', 'nut butter', 'nutella'] },
  { category: 'Dips',                       racc: 30,  note: '2 tbsp',                        keywords: ['dip', 'salsa', 'hummus', 'guacamole', 'queso'] },
  // Candy & confections
  { category: 'Candy, chocolate',           racc: 40,  note: 'approx.',                       keywords: ['chocolate', 'chocolate bar', 'truffle', 'bonbon'] },
  { category: 'Candy, non-chocolate',       racc: 40,  note: 'approx.',                       keywords: ['candy', 'gummy', 'hard candy', 'lollipop', 'gumdrop', 'caramel'] },
  // Beverages
  { category: 'Carbonated / soft drinks',   racc: 360, note: '12 fl oz',                      keywords: ['soda', 'soft drink', 'pop', 'sports drink', 'lemonade'] },
  { category: 'Coffee / tea, brewed',       racc: 240, note: '8 fl oz',                       keywords: ['coffee', 'brewed coffee', 'iced tea', 'tea'] },
];

// ─────────────────────────────────────────────────────────────────────────────
// RaccLookup — small inline search that suggests FDA serving sizes
// ─────────────────────────────────────────────────────────────────────────────
// Normalizes punctuation (hyphens, slashes, extra spaces) so "ice-cream" matches the keyword
// "ice cream", "pop-tart" matches "pop tart", etc.
const normalizeRaccText = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const RaccLookup = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const results = query.trim().length < 2 ? [] : (() => {
    const q = normalizeRaccText(query);
    return FDA_RACC.filter(r =>
      normalizeRaccText(r.category).includes(q) ||
      r.keywords.some(k => {
        const nk = normalizeRaccText(k);
        return nk.includes(q) || q.includes(nk);
      })
    ).slice(0, 6);
  })();

  return (
    <div className="mb-3">
      <label className="form-label form-label-sm fw-semibold">
        FDA RACC Lookup <span className="text-muted fw-normal">(21 CFR 101.12)</span>
      </label>
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder='Search product type, e.g. "gelato", "yogurt", "chicken"…'
        value={query}
        onChange={e => setQuery(e.target.value)}
      />
      <div className="form-text" style={{ fontSize: '11px' }}>
        Starting point only — spot-check the selected value against the current FDA reference
        amounts table before finalizing a label, especially for volume-based categories
        (e.g. ice cream) where actual gram weight depends on your product's density.
      </div>
      {results.length > 0 && (
        <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', marginTop: '4px',
          background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }}>
          {results.map((r, i) => (
            <div key={i}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '6px 10px', borderBottom: i < results.length - 1 ? '1px solid #f0f0f0' : 'none',
                cursor: 'pointer', fontSize: '12px' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f0f7ff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
              onClick={() => { onSelect(r); setQuery(''); }}
            >
              <div>
                <span style={{ fontWeight: 500 }}>{r.category}</span>
                <span className="text-muted ms-2">{r.note}</span>
              </div>
              <span className="badge bg-primary ms-2" style={{ fontSize: '11px' }}>
                {r.racc}g
              </span>
            </div>
          ))}
        </div>
      )}
      {query.trim().length >= 2 && results.length === 0 && (
        <div className="form-text text-muted">No matching FDA category found.</div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FDA Daily Value reference amounts (21 CFR 101.9(c)(8)(iv))
// ─────────────────────────────────────────────────────────────────────────────
const DAILY_VALUES = {
  totalFat: 78, saturatedFat: 20, cholesterol: 300, sodium: 2300,
  totalCarbs: 275, dietaryFiber: 28, addedSugars: 50,
  vitaminD: 20, calcium: 1300, iron: 18, potassium: 4700,
};

// FDA 21 CFR 101.9(c)(8)(iii) rounding for vitamins & minerals — MUST run on the raw (unrounded)
// %DV ratio, never a value already rounded to a whole percent: rounding first and then tiering
// double-rounds borderline values into the wrong band (e.g. a true 2.5% DV would naive-round to
// 3% first, then tier to 4% — wrong; tiering the raw 2.5 directly gives the correct 2%).
// <2% → 0, 2–10% → nearest 2%, 10–50% → nearest 5%, >50% → nearest 10%
const fdaRoundMicroDV = (rawPct) => {
  if (rawPct == null) return null;
  if (rawPct < 2) return 0;
  if (rawPct <= 10) return Math.round(rawPct / 2) * 2;
  if (rawPct <= 50) return Math.round(rawPct / 5) * 5;
  return Math.round(rawPct / 10) * 10;
};

const dvPct = (val, key) => {
  if (val == null || !DAILY_VALUES[key]) return null;
  return Math.round((Number(val) / DAILY_VALUES[key]) * 100);
};

// Nutrients that can be individually pinned, in Nutrition Facts display order.
const OVERRIDABLE_NUTRIENTS = [
  ['calories', 'Calories', ''],
  ['totalFat', 'Total Fat', 'g'],
  ['saturatedFat', 'Saturated Fat', 'g'],
  ['transFat', 'Trans Fat', 'g'],
  ['cholesterol', 'Cholesterol', 'mg'],
  ['sodium', 'Sodium', 'mg'],
  ['totalCarbs', 'Total Carbohydrate', 'g'],
  ['dietaryFiber', 'Dietary Fibre', 'g'],
  ['totalSugars', 'Total Sugars', 'g'],
  ['addedSugars', 'Added Sugars', 'g'],
  ['protein', 'Protein', 'g'],
  ['vitaminD', 'Vitamin D', 'mcg'],
  ['calcium', 'Calcium', 'mg'],
  ['iron', 'Iron', 'mg'],
  ['potassium', 'Potassium', 'mg'],
];

// Strip blanks/non-numerics so an empty input clears the pin rather than pinning NaN.
const cleanOverrides = (o) => Object.fromEntries(
  Object.entries(o || {})
    .filter(([, v]) => v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v)))
    .map(([k, v]) => [k, Number(v)])
);

// dvPct for vitamins/minerals — applies FDA stepping to the raw ratio directly (never the
// whole-percent-rounded dvPct() above, which would double-round borderline values).
const microDvPct = (val, key) => {
  if (val == null || !DAILY_VALUES[key]) return null;
  const raw = (Number(val) / DAILY_VALUES[key]) * 100;
  return fdaRoundMicroDV(raw);
};

// ─────────────────────────────────────────────────────────────────────────────
// FDA Nutrition Facts Label — pure display component
// ─────────────────────────────────────────────────────────────────────────────
const NutritionLabel = ({ profile, compact = false }) => {
  if (!profile) return null;
  const s = profile; // shorthand

  const fmt = (v, decimals = 0) =>
    v == null ? '—' : Number(v).toFixed(decimals).replace(/\.0+$/, '');

  const pct = (key) => {
    const p = dvPct(s[key], key);
    return p == null ? '' : `${p}%`;
  };

  const labelStyle = {
    fontFamily: 'Arial, Helvetica, sans-serif',
    border: '1px solid #000',
    padding: compact ? '6px 8px' : '8px 10px',
    width: compact ? '240px' : '280px',
    fontSize: compact ? '11px' : '12px',
    lineHeight: '1.3',
    color: '#000',
    background: '#fff',
  };

  const thick = { borderBottom: '8px solid #000' };
  const medium = { borderBottom: '4px solid #000' };
  const thin = { borderBottom: '1px solid #000' };
  const hairline = { borderBottom: '0.5px solid #000' };

  const row = (label, value, pctVal, bold = false, indent = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      paddingLeft: indent ? '16px' : '0', ...hairline, paddingTop: '1px', paddingBottom: '1px' }}>
      <span style={{ fontWeight: bold ? 'bold' : 'normal' }}>{label} <strong>{value}</strong></span>
      {pctVal != null && <span style={{ fontWeight: 'bold' }}>{pctVal}</span>}
    </div>
  );

  return (
    <div style={labelStyle}>
      <div style={{ fontSize: compact ? '22px' : '28px', fontWeight: 'black', fontStyle: 'italic',
        borderBottom: '1px solid #000', paddingBottom: '2px', marginBottom: '2px' }}>
        Nutrition Facts
      </div>

      <div style={{ fontSize: compact ? '9px' : '10px', ...hairline, paddingBottom: '2px' }}>
        {s.servingsPerContainer ? `${fmt(s.servingsPerContainer, 1)} servings per container` : ''}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        ...thick, paddingBottom: '2px' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: compact ? '11px' : '12px' }}>Serving size</div>
          <div style={{ fontSize: compact ? '9px' : '10px' }}>
            {s.servingSizeLabel || `${fmt(s.servingSize, 1)}g`}
          </div>
        </div>
        <div style={{ fontWeight: 'black', fontSize: compact ? '16px' : '20px' }}>
          {s.servingSizeLabel ? '' : `${fmt(s.servingSize, 0)}g`}
        </div>
      </div>

      <div style={{ fontSize: compact ? '9px' : '10px', fontWeight: 'bold', paddingTop: '1px' }}>
        Amount per serving
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        ...medium, paddingBottom: '2px' }}>
        <div style={{ fontWeight: 'black', fontSize: compact ? '20px' : '24px' }}>Calories</div>
        <div style={{ fontWeight: 'black', fontSize: compact ? '28px' : '36px' }}>
          {fmt(s.calories)}
        </div>
      </div>

      <div style={{ textAlign: 'right', fontSize: compact ? '9px' : '10px',
        fontWeight: 'bold', ...thin, paddingBottom: '1px' }}>
        % Daily Value*
      </div>

      {row('Total Fat', `${fmt(s.totalFat, 1)}g`, pct('totalFat'), true)}
      {row('Saturated Fat', `${fmt(s.saturatedFat, 1)}g`, pct('saturatedFat'), false, true)}
      {row('Trans Fat', `${fmt(s.transFat, 1)}g`, null, false, true)}
      {row('Cholesterol', `${fmt(s.cholesterol, 0)}mg`, pct('cholesterol'), true)}
      {row('Sodium', `${fmt(s.sodium, 0)}mg`, pct('sodium'), true)}
      {row('Total Carbohydrate', `${fmt(s.totalCarbs, 1)}g`, pct('totalCarbs'), true)}
      {row('Dietary Fiber', `${fmt(s.dietaryFiber, 1)}g`, pct('dietaryFiber'), false, true)}
      {row('Total Sugars', `${fmt(s.totalSugars, 1)}g`, null, false, true)}
      <div style={{ paddingLeft: '32px', ...hairline, paddingTop: '1px', paddingBottom: '1px',
        fontSize: compact ? '10px' : '11px' }}>
        Includes {fmt(s.addedSugars, 0)}g Added Sugars <span style={{ fontWeight: 'bold' }}>
          {pct('addedSugars')}
        </span>
      </div>
      {row('Protein', `${fmt(s.protein, 1)}g`, null, true)}

      <div style={{ ...thick }} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', ...hairline }}>
        <div style={{ borderRight: '1px solid #000', paddingRight: '4px' }}>
          Vitamin D {fmt(s.vitaminD, 1)}mcg <strong>{microDvPct(s.vitaminD, 'vitaminD')}%</strong>
        </div>
        <div style={{ paddingLeft: '4px' }}>
          Calcium {fmt(s.calcium, 0)}mg <strong>{microDvPct(s.calcium, 'calcium')}%</strong>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <div style={{ borderRight: '1px solid #000', paddingRight: '4px' }}>
          Iron {fmt(s.iron, 1)}mg <strong>{microDvPct(s.iron, 'iron')}%</strong>
        </div>
        <div style={{ paddingLeft: '4px' }}>
          Potassium {fmt(s.potassium, 0)}mg <strong>{microDvPct(s.potassium, 'potassium')}%</strong>
        </div>
      </div>

      <div style={{ borderTop: '4px solid #000', fontSize: compact ? '8px' : '9px',
        paddingTop: '2px', lineHeight: '1.2' }}>
        *The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a
        daily diet. 2,000 calories a day is used for general nutrition advice.
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Ingredient Nutrition Editor (per-ingredient, inside the 🧪 tab)
// ─────────────────────────────────────────────────────────────────────────────
const IngredientNutritionRow = ({
  ingredient,
  onSaved,
  badge = null,
  defaultExpanded = false,
  prefillData = null,
  prefillConfidence = null,
  prefillPlausibility = null,
  readOnly = false,
  onNavigateToIngredient = null,
  onDirty = null,
  onClean = null,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [nutrition, setNutrition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [importing, setImporting] = useState(false);
  const [fdcQuery, setFdcQuery] = useState('');
  const [fdcResults, setFdcResults] = useState([]);
  const [fdcSearching, setFdcSearching] = useState(false);
  const [fdcError, setFdcError] = useState(''); // separate from save errors
  const [form, setForm] = useState({});

  // Fat sub-profile derivation panel
  const [fatPanelOpen, setFatPanelOpen] = useState(false);
  const [fatQ, setFatQ] = useState('');
  const [fatResults, setFatResults] = useState([]);
  const [fatSearching, setFatSearching] = useState(false);
  const [fatDeriving, setFatDeriving] = useState(false);
  const [fatError, setFatError] = useState('');
  const [showFatNote, setShowFatNote] = useState(false);
  const [specServingG, setSpecServingG] = useState(''); // spec sheet serving size, blank = 100g
  const [saveError, setSaveError] = useState('');

  // AI micronutrient plausibility check (vitamin D / calcium / iron / potassium)
  const [plausibility, setPlausibility] = useState(null);
  const [plausibilityLoading, setPlausibilityLoading] = useState(false);
  const [plausibilityError, setPlausibilityError] = useState('');

  // Spec sheet document reference
  const [specDocId, setSpecDocId]     = useState('');
  const [specDocName, setSpecDocName] = useState('');
  const [specDocDate, setSpecDocDate] = useState('');

  const FIELDS = [
    { key: 'calories',           label: 'Calories',             unit: 'kcal' },
    { key: 'totalFat',           label: 'Total Fat',             unit: 'g' },
    { key: 'saturatedFat',       label: 'Saturated Fat',         unit: 'g' },
    { key: 'transFat',           label: 'Trans Fat',             unit: 'g' },
    { key: 'polyunsaturatedFat', label: 'Polyunsaturated Fat',   unit: 'g' },
    { key: 'monounsaturatedFat', label: 'Monounsaturated Fat',   unit: 'g' },
    { key: 'cholesterol',        label: 'Cholesterol',           unit: 'mg' },
    { key: 'sodium',             label: 'Sodium',                unit: 'mg' },
    { key: 'totalCarbs',         label: 'Total Carbohydrate',    unit: 'g' },
    { key: 'dietaryFiber',       label: 'Dietary Fiber',         unit: 'g' },
    { key: 'totalSugars',        label: 'Total Sugars',          unit: 'g' },
    { key: 'addedSugars',        label: 'Added Sugars',          unit: 'g' },
    { key: 'protein',            label: 'Protein',               unit: 'g' },
    { key: 'vitaminD',           label: 'Vitamin D',             unit: 'mcg' },
    { key: 'calcium',            label: 'Calcium',               unit: 'mg' },
    { key: 'iron',               label: 'Iron',                  unit: 'mg' },
    { key: 'potassium',          label: 'Potassium',             unit: 'mg' },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setPlausibility(null);
    setPlausibilityError('');
    try {
      const data = await getIngredientNutrition(ingredient.ingredientId);
      setNutrition(data);
      const f = {};
      for (const { key } of FIELDS) f[key] = data?.[key] ?? '';
      setForm(f);
      setSpecDocId(data?.specSheetDocumentId ? String(data.specSheetDocumentId) : '');
      setSpecDocName(data?.specSheetDocumentName || '');
      setSpecDocDate(data?.specSheetDate || '');
    } catch { /* ignore */ }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredient.ingredientId]);

  useEffect(() => { load(); }, [load]);

  // When parent passes AI-extracted data, pre-fill the form and expand the panel
  useEffect(() => {
    if (!prefillData) return;
    setForm(prev => {
      const updated = { ...prev };
      FIELDS.forEach(({ key }) => {
        if (prefillData[key] != null) updated[key] = String(prefillData[key]);
      });
      return updated;
    });
    if (!expanded) setExpanded(true);
    onDirty?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillData]);

  // AI spec-sheet extraction runs this same plausibility check automatically — pick it up here
  // rather than making the user click "Check Plausibility" again right after importing.
  useEffect(() => {
    if (prefillPlausibility) setPlausibility(prefillPlausibility);
  }, [prefillPlausibility]);

  const handleCheckPlausibility = async () => {
    setPlausibilityLoading(true);
    setPlausibilityError('');
    try {
      setPlausibility(await checkNutritionPlausibility(ingredient.ingredientId));
    } catch (e) {
      setPlausibilityError(e.response?.data?.error || e.message || 'Plausibility check failed.');
    }
    setPlausibilityLoading(false);
  };

  const handleFdcSearch = async () => {
    if (!fdcQuery.trim()) return;
    setFdcSearching(true);
    setFdcResults([]);
    setFdcError('');
    try {
      const results = await searchFdc(fdcQuery);
      if (results.length === 0) setFdcError('No results found. Try a different search term.');
      setFdcResults(results.slice(0, 8));
    } catch (e) {
      console.error('[FDC search]', e);
      setFdcError(e.response?.data?.error || e.message || 'FDC search failed — check your connection.');
    }
    setFdcSearching(false);
  };

  const handleFdcImport = async (fdcId) => {
    setImporting(true);
    setFdcError('');
    try {
      const { nutrition: saved } = await importFromFdc(ingredient.ingredientId, fdcId);
      setNutrition(saved);
      const f = {};
      for (const { key } of FIELDS) f[key] = saved?.[key] ?? '';
      setForm(f);
      setFdcResults([]);
      setFdcQuery('');
      setSpecServingG(''); // FDC values are already per-100g
      onSaved?.();
    } catch (e) {
      console.error('[FDC import]', e);
      setFdcError(e.response?.data?.error || e.message || 'Import failed.');
    }
    setImporting(false);
  };

  const handleUnlinkFdc = async () => {
    setSaveError('');
    try {
      const saved = await saveIngredientNutrition(ingredient.ingredientId, {
        fdcId: null, fdcDescription: null, dataSource: 'manual',
      });
      setNutrition(saved);
    } catch (e) { setSaveError(e.response?.data?.error || e.message); }
  };

  const handleFatSearch = async (overrideQ) => {
    const q = (overrideQ ?? fatQ).trim() || ingredient.name;
    setFatSearching(true);
    setFatResults([]);
    setFatError('');
    try {
      const results = await searchFdc(q);
      if (!results.length) setFatError('No results. Try a more specific search term.');
      setFatResults(results.slice(0, 8));
    } catch (e) {
      setFatError(e.response?.data?.error || e.message || 'FDC search failed.');
    }
    setFatSearching(false);
  };

  const handleDeriveFat = async (fdcId) => {
    setFatDeriving(true);
    setFatError('');
    try {
      const { nutrition: saved } = await deriveFatProfile(ingredient.ingredientId, fdcId);
      setNutrition(saved);
      const f = {};
      for (const { key } of FIELDS) f[key] = saved?.[key] ?? '';
      setForm(f);
      // Collapse the panel — success is visible via the source badge
      setFatPanelOpen(false);
      setFatResults([]);
      setFatQ('');
      setShowFatNote(false);
      onSaved?.();
    } catch (e) {
      setFatError(e.response?.data?.error || e.message || 'Derivation failed.');
    }
    setFatDeriving(false);
  };

  const flashSaved = () => {
    setSavedOk(true);
    setTimeout(() => setSavedOk(false), 2500);
  };

  const doSave = async () => {
    setShowSaveConfirm(false);
    setSaving(true);
    setSavedOk(false);
    setSaveError('');
    try {
      // If a spec-sheet serving size is set, normalize values to per-100g before saving
      const factor = specServingG && Number(specServingG) > 0
        ? 100 / Number(specServingG)
        : 1;
      // Only mark as 'fdc_overridden' if the user actually changed a value from what FDC provided
      let dataSource = 'manual';
      if (nutrition?.fdcId) {
        const valuesChanged = factor !== 1 || FIELDS.some(({ key }) => {
          const formVal = form[key] === '' ? null : Math.round(Number(form[key]) * factor * 10000) / 10000;
          const savedVal = nutrition[key] != null ? Math.round(Number(nutrition[key]) * 10000) / 10000 : null;
          return formVal !== savedVal;
        });
        dataSource = valuesChanged ? 'fdc_overridden' : 'fdc';
      }
      const data = { dataSource };
      for (const { key } of FIELDS) {
        data[key] = form[key] === '' ? null : Math.round(Number(form[key]) * factor * 10000) / 10000;
      }
      // Spec sheet reference
      data.specSheetDocumentId   = specDocId   ? Number(specDocId)   : null;
      data.specSheetDocumentName = specDocName || null;
      data.specSheetDate         = specDocDate || null;
      const saved = await saveIngredientNutrition(ingredient.ingredientId, data);
      setNutrition(saved);
      flashSaved();
      onSaved?.();
      onClean?.();
    } catch (e) {
      console.error('[Nutrition save]', e);
      setSaveError(e.response?.data?.error || e.message || 'Save failed.');
    }
    setSaving(false);
  };

  const handleSave = () => setShowSaveConfirm(true);

  const hasData = !!nutrition?.calories;

  return (
    <>
    <ConfirmModal
      show={showSaveConfirm}
      title="Update ingredient nutrition?"
      message={
        <span>
          Changes to <strong>{ingredient.name}</strong>'s nutrition will recalculate the nutrition facts
          for <strong>all products</strong> that use this ingredient.
          Approved labels will not be affected.
        </span>
      }
      confirmLabel="Save changes"
      variant="primary"
      onConfirm={doSave}
      onCancel={() => setShowSaveConfirm(false)}
    />
    <div style={{ border: '1px solid #dee2e6', borderRadius: '6px', marginBottom: '6px' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
          cursor: 'pointer', background: '#f8f9fa', borderRadius: '6px' }}
        onClick={() => setExpanded(v => !v)}
      >
        <span style={{ flex: 1, fontWeight: 500 }}>{ingredient.name}{badge}</span>
        {nutrition?.fdcId && (
          <span className="badge bg-primary" style={{ fontSize: '10px' }}>USDA</span>
        )}
        {nutrition?.dataSource === 'manual' && hasData && (
          <span className="badge bg-warning text-dark" style={{ fontSize: '10px' }}>custom</span>
        )}
        {/* Fat sub-profile status badge */}
        {nutrition && Number(nutrition.totalFat) >= 0.1 && (() => {
          const allSubFatsSet = nutrition.saturatedFat != null && nutrition.transFat != null
            && nutrition.polyunsaturatedFat != null && nutrition.monounsaturatedFat != null;
          if (!allSubFatsSet) return (
            <span className="badge bg-warning text-dark" style={{ fontSize: '10px' }} title="Fat sub-profile incomplete — sat/trans/poly/mono missing">
              ⚠ fat
            </span>
          );
          if (nutrition.fatProfileSource === 'fdc_derived') return (
            <span className="badge" style={{ background: '#d97706', color: '#fff', fontSize: '10px' }} title="Fat sub-profile derived from USDA reference">
              fat ✓ USDA
            </span>
          );
          return (
            <span className="badge bg-success" style={{ fontSize: '10px' }} title="Fat sub-profile complete">
              fat ✓
            </span>
          );
        })()}
        <span style={{ fontSize: '11px', color: hasData ? '#198754' : '#6c757d' }}>
          {hasData ? '✓ data set' : 'no data'}
        </span>
        <span>{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div style={{ padding: '12px' }}>
          {loading ? (
            <div className="text-muted small">Loading…</div>
          ) : readOnly ? (
            /* ── Read-only view (product context) ── */
            <div>
              <div className="d-flex align-items-start gap-2 mb-3 p-2 rounded"
                style={{ background: '#f0f7ff', border: '1px solid #bfdbfe', fontSize: 12 }}>
                <span style={{ fontSize: 15 }}>ℹ️</span>
                <span className="text-muted">
                  Nutrition data is managed in the <strong>Ingredients Hub</strong>.
                  Changes there update all recipes that use this ingredient automatically.
                </span>
              </div>
              {hasData ? (
                <div className="d-flex flex-wrap gap-2 mb-3" style={{ fontSize: 12 }}>
                  {FIELDS
                    .filter(({ key }) => nutrition[key] != null && Number(nutrition[key]) !== 0)
                    .map(({ key, label, unit }) => (
                      <div key={key} className="text-center px-2 py-1 rounded"
                        style={{ background: '#f8f9fa', border: '1px solid #dee2e6', minWidth: 58 }}>
                        <div style={{ fontWeight: 600 }}>
                          {nutrition[key]}
                          <span className="text-muted ms-1" style={{ fontWeight: 400 }}>{unit}</span>
                        </div>
                        <div className="text-muted" style={{ fontSize: 10 }}>{label}</div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-muted small mb-3">No nutrition data set for this ingredient yet.</div>
              )}
              {onNavigateToIngredient && (
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => onNavigateToIngredient(ingredient.ingredientId)}
                >
                  ✎ Edit nutrition in Ingredients Hub →
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Reuse note */}
              <div className="alert alert-light border py-1 px-2 mb-2" style={{ fontSize: '11px', color: '#495057' }}>
                Data saved here is shared across <strong>all recipes</strong> that use this ingredient —
                enter it once and it will be reused automatically.
              </div>

              {/* AI prefill confidence banner */}
              {prefillConfidence && (
                <div
                  className={`alert py-2 px-3 mb-2 small ${prefillConfidence === 'high' ? 'alert-info' : 'alert-warning'}`}
                  style={{ fontSize: '12px' }}
                >
                  <strong>✨ AI-extracted values pre-filled</strong>
                  {prefillConfidence !== 'high' && (
                    <span className="ms-1">(confidence: {prefillConfidence} — review carefully)</span>
                  )}
                  . Review and click Save to confirm.
                </div>
              )}

              {/* Micronutrient plausibility check — catches a spec sheet that simply omitted
                  vitamin D/calcium/iron/potassium (saved as a false zero) vs. a genuinely normal
                  one, using standard food-composition knowledge rather than a flat zero-check. */}
              <div className="d-flex align-items-center gap-2 mb-2">
                <button
                  className="btn btn-outline-secondary btn-sm py-0 px-2"
                  style={{ fontSize: '11px' }}
                  onClick={handleCheckPlausibility}
                  disabled={plausibilityLoading}
                >
                  {plausibilityLoading ? '🔍 Checking…' : '🔍 Check Vitamin/Mineral Plausibility'}
                </button>
              </div>
              {plausibilityError && (
                <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: '12px' }}>{plausibilityError}</div>
              )}
              {plausibility?.summary && (
                <div className="alert alert-light border py-2 px-3 mb-2" style={{ fontSize: '12px' }}>
                  <strong>🔍 AI plausibility check:</strong> {plausibility.summary}
                </div>
              )}

              {/* FDC source badge + unlink */}
              {nutrition?.fdcId && (
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="badge bg-primary" style={{ fontSize: '11px' }}>
                    USDA FDC #{nutrition.fdcId}
                  </span>
                  {nutrition.fdcDescription && (
                    <span style={{ fontSize: '12px', color: '#495057' }}>{nutrition.fdcDescription}</span>
                  )}
                  <button
                    className="btn btn-outline-secondary btn-sm py-0 px-2 ms-auto"
                    style={{ fontSize: '11px' }}
                    onClick={handleUnlinkFdc}
                    title="Remove FDC link and switch to manual data"
                  >
                    × Unlink FDC
                  </button>
                </div>
              )}

              {/* FDC Search */}
              <div className="mb-3">
                <label className="form-label small fw-semibold mb-1">
                  {nutrition?.fdcId ? 'Replace with a different USDA item' : 'Import from USDA FoodData Central'}
                </label>
                <div className="d-flex gap-2">
                  <input
                    className="form-control form-control-sm"
                    placeholder={`Search "${ingredient.name}"…`}
                    value={fdcQuery}
                    onChange={e => { setFdcQuery(e.target.value); if (!e.target.value) { setFdcResults([]); setFdcError(''); } }}
                    onKeyDown={e => e.key === 'Enter' && handleFdcSearch()}
                  />
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleFdcSearch}
                    disabled={fdcSearching}>
                    {fdcSearching ? '…' : 'Search'}
                  </button>
                </div>
                {fdcError && (
                  <div className="alert alert-danger py-1 px-2 mt-1 mb-0" style={{ fontSize: '12px' }}>
                    {fdcError}
                  </div>
                )}
                {fdcResults.length > 0 && (
                  <div style={{ border: '1px solid #dee2e6', borderRadius: '4px',
                    maxHeight: '140px', overflowY: 'auto', marginTop: '4px' }}>
                    {fdcResults.map(r => (
                      <div key={r.fdcId} style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #f0f0f0',
                        gap: '8px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '12px', fontWeight: 500 }}>{r.description}</div>
                          <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '1px' }}>
                            <span className={`badge me-1 ${
                              r.dataType === 'SR Legacy'   ? 'bg-success' :
                              r.dataType === 'Foundation'  ? 'bg-primary' :
                              r.dataType === 'Branded'     ? 'bg-secondary' : 'bg-light text-dark border'
                            }`} style={{ fontSize: '10px' }}>
                              {r.dataType}
                            </span>
                            {r.brandName && <span className="me-1">{r.brandName}</span>}
                            {!r.brandName && r.brandOwner && <span className="me-1">{r.brandOwner}</span>}
                            {r.foodCategory && <span className="text-muted">· {r.foodCategory}</span>}
                            {r.servingSize && <span className="text-muted ms-1">· {r.servingSize}/serving</span>}
                          </div>
                        </div>
                        <button className="btn btn-outline-primary btn-sm py-0 px-2"
                          style={{ flexShrink: 0 }}
                          disabled={importing}
                          onClick={() => handleFdcImport(r.fdcId)}>
                          Use
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Manual fields */}
              {/* Spec sheet serving size helper */}
              <div className="d-flex align-items-center gap-2 mb-2 p-2 rounded"
                style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', marginBottom: 0 }}>
                  Spec sheet serving size:
                </label>
                <div className="d-flex align-items-center gap-1" style={{ width: 100 }}>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    className="form-control form-control-sm"
                    placeholder="100"
                    value={specServingG}
                    onChange={e => setSpecServingG(e.target.value)}
                    style={{ width: 70 }}
                  />
                  <span style={{ fontSize: '12px' }}>g</span>
                </div>
                <span style={{ fontSize: '11px', color: '#6c757d' }}>
                  {specServingG && Number(specServingG) > 0 && Number(specServingG) !== 100
                    ? <>Enter values <strong>as listed on the spec sheet</strong> — will be auto-converted to per-100g on save
                      {' '}(× {(100 / Number(specServingG)).toFixed(4).replace(/\.?0+$/, '')})</>
                    : 'Enter values per 100g, or set the spec sheet serving size to convert automatically'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px',
                marginBottom: '10px' }}>
                {FIELDS.map(({ key, label, unit }) => {
                  const factor = specServingG && Number(specServingG) > 0 ? 100 / Number(specServingG) : 1;
                  const rawVal = form[key];
                  const per100 = rawVal !== '' && rawVal != null && factor !== 1
                    ? Math.round(Number(rawVal) * factor * 100) / 100
                    : null;
                  const assessment = plausibility?.assessments?.find(a => a.nutrient === key);
                  return (
                    <div key={key}>
                      <label style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px',
                        display: 'block' }}>
                        {label}
                        <span className="text-muted ms-1">
                          ({unit}/{specServingG && Number(specServingG) > 0 && Number(specServingG) !== 100
                            ? `${specServingG}g` : '100g'})
                        </span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control form-control-sm"
                        value={form[key] ?? ''}
                        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); onDirty?.(); }}
                      />
                      {per100 !== null && (
                        <div style={{ fontSize: '10px', color: '#0d6efd', marginTop: '1px' }}>
                          → {per100}/100g
                        </div>
                      )}
                      {assessment && assessment.status !== 'plausible' && (
                        <div
                          className={assessment.status === 'likely_missing' ? 'text-warning' : 'text-danger'}
                          style={{ fontSize: '10px', marginTop: '1px' }}
                          title={assessment.note}
                        >
                          {assessment.status === 'likely_missing' ? '⚠ likely missing' : '⚠ suspicious'}
                          {assessment.suggestedValue != null && ` — try ~${assessment.suggestedValue}`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Fat Sub-Profile Derivation Panel */}
              {Number(nutrition?.totalFat) > 0 && (
                <div className="p-2 mb-2 rounded" style={{ background: '#fff8f0', border: '1px solid #fed7aa' }}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <div className="fw-semibold" style={{ fontSize: '12px', color: '#92400e' }}>
                      Fat Sub-Profile
                    </div>
                    {nutrition?.fatProfileSource === 'fdc_derived' ? (
                      <span className="badge" style={{ background: '#d97706', fontSize: '10px' }}>USDA-derived</span>
                    ) : nutrition?.fatProfileSource === 'spec_sheet' ? (
                      <span className="badge bg-success" style={{ fontSize: '10px' }}>Spec sheet</span>
                    ) : (
                      <span className="badge bg-secondary" style={{ fontSize: '10px' }}>No source</span>
                    )}
                    <button
                      className="btn btn-link btn-sm p-0 ms-auto"
                      style={{ fontSize: '11px' }}
                      onClick={() => {
                        const opening = !fatPanelOpen;
                        setFatPanelOpen(opening);
                        if (opening && !fatResults.length && !fatSearching) {
                          setFatQ(ingredient.name);
                          handleFatSearch(ingredient.name);
                        }
                      }}
                    >
                      {fatPanelOpen ? 'Close ▲' : (nutrition?.fatProfileSource ? 'Change reference ▼' : 'Derive from USDA ▼')}
                    </button>
                  </div>

                  {nutrition?.fatProfileSource === 'fdc_derived' && nutrition?.fatProfileFdcDescription && !fatPanelOpen && (
                    <div style={{ fontSize: '11px', color: '#78350f' }}>
                      Reference: {nutrition.fatProfileFdcDescription} (FDC #{nutrition.fatProfileFdcId})
                      {' · '}
                      <button className="btn btn-link p-0" style={{ fontSize: '11px' }}
                        onClick={() => { setShowFatNote(v => !v); }}>
                        {showFatNote ? 'Hide justification' : 'View justification'}
                      </button>
                    </div>
                  )}

                  {showFatNote && nutrition?.fatProfileNote && !fatPanelOpen && (
                    <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', background: '#fff', border: '1px solid #fed7aa',
                      borderRadius: 4, padding: '6px 8px', marginTop: 6, color: '#374151', maxHeight: 180, overflowY: 'auto' }}>
                      {nutrition.fatProfileNote}
                    </pre>
                  )}

                  {fatPanelOpen && (
                    <div className="mt-2">
                      <div style={{ background: '#fff', border: '1px solid #fed7aa', borderRadius: 4,
                        padding: '5px 8px', marginBottom: 6, fontSize: '11px', color: '#92400e' }}>
                        <strong>Saved total fat: {Number(nutrition.totalFat).toFixed(2)}g/100g</strong>
                        {' — '}ratios from the USDA match will be applied to this value proportionally.
                        {form.totalFat !== '' && Math.abs(parseFloat(form.totalFat) - Number(nutrition.totalFat)) > 0.001 && (
                          <span className="ms-2" style={{ color: '#dc2626' }}>
                            ⚠ Your form shows {parseFloat(form.totalFat)}g — save the form first to use this value.
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#78350f', marginBottom: 6 }}>
                        Pick the closest USDA match below. SR Legacy and Foundation foods have the most complete fat sub-component data.
                      </div>
                      <div className="d-flex gap-2 mb-1">
                        <input
                          className="form-control form-control-sm"
                          placeholder="Search USDA…"
                          value={fatQ}
                          onChange={e => { setFatQ(e.target.value); setFatResults([]); setFatError(''); }}
                          onKeyDown={e => e.key === 'Enter' && handleFatSearch()}
                        />
                        <button className="btn btn-warning btn-sm" style={{ whiteSpace: 'nowrap' }}
                          onClick={() => handleFatSearch()} disabled={fatSearching}>
                          {fatSearching ? 'Searching…' : 'Search'}
                        </button>
                      </div>
                      {fatError && (
                        <div className="alert alert-danger py-1 px-2 mb-1" style={{ fontSize: '11px' }}>{fatError}</div>
                      )}
                      {fatResults.length > 0 && (
                        <div style={{ border: '1px solid #fed7aa', borderRadius: 4, maxHeight: 200, overflowY: 'auto' }}>
                          {fatResults.map(r => {
                            const hasFullFat = r.dataType === 'SR Legacy' || r.dataType === 'Foundation';
                            return (
                            <div key={r.fdcId} style={{ display: 'flex', alignItems: 'center', gap: 8,
                              padding: '5px 8px', borderBottom: '1px solid #fef3c7', fontSize: 12,
                              background: hasFullFat ? '#fffdf5' : '#fafafa' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500 }}>{r.description}</div>
                                <div style={{ fontSize: 10, marginTop: 2 }}>
                                  <span className={`badge me-1 ${r.dataType === 'SR Legacy' ? 'bg-success' : r.dataType === 'Foundation' ? 'bg-primary' : 'bg-secondary'}`}
                                    style={{ fontSize: 9 }}>{r.dataType}</span>
                                  {r.foodCategory && <span className="text-muted">{r.foodCategory}</span>}
                                  {!hasFullFat && (
                                    <span className="ms-1 text-danger" style={{ fontSize: 9, fontWeight: 600 }}>
                                      ✗ no poly/mono data
                                    </span>
                                  )}
                                  {hasFullFat && (
                                    <span className="ms-1 text-success" style={{ fontSize: 9 }}>
                                      ✓ full fat profile
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button className="btn btn-warning btn-sm py-0 px-2" style={{ flexShrink: 0, fontSize: 11 }}
                                disabled={fatDeriving || !hasFullFat}
                                title={!hasFullFat ? 'This reference lacks poly/mono fat data — choose SR Legacy or Foundation' : ''}
                                onClick={() => handleDeriveFat(r.fdcId)}>
                                {fatDeriving ? '…' : 'Apply'}
                              </button>
                            </div>
                            );
                          })}
                        </div>
                      )}
                      {nutrition?.fatProfileNote && (
                        <div className="mt-2">
                          <button className="btn btn-link p-0" style={{ fontSize: '11px' }}
                            onClick={() => setShowFatNote(v => !v)}>
                            {showFatNote ? 'Hide justification ▲' : 'View current justification ▼'}
                          </button>
                          {showFatNote && (
                            <pre style={{ fontSize: '10px', whiteSpace: 'pre-wrap', background: '#fff', border: '1px solid #fed7aa',
                              borderRadius: 4, padding: '6px 8px', marginTop: 4, color: '#374151', maxHeight: 180, overflowY: 'auto' }}>
                              {nutrition.fatProfileNote}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {saveError && <div className="alert alert-danger py-1 px-2 mb-2 small">{saveError}</div>}

              <div className="d-flex align-items-center gap-3">
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {savedOk && (
                  <span style={{ fontSize: '12px', color: '#198754', fontWeight: 500 }}>
                    ✓ Saved
                  </span>
                )}
                {!nutrition?.fdcId && !hasData && (
                  <span style={{ fontSize: '11px', color: '#6c757d' }}>
                    Enter values manually or import from USDA above
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// NutritionFactsPanel — main component
// ─────────────────────────────────────────────────────────────────────────────
const NutritionFactsPanel = ({ product, productIngredients = [], onNavigateToIngredient = null }) => {
  const [profile, setProfile] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [versions, setVersions] = useState([]);
  const [activeTab, setActiveTab] = useState('label'); // 'label' | 'ingredients' | 'settings'
  const [rawMode, setRawMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [approveSuccess, setApproveSuccess] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [error, setError] = useState('');
  const [reconciliationErrors, setReconciliationErrors] = useState([]);
  const [printReady, setPrintReady] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);

  // Manual override
  const [manualMode, setManualMode] = useState(false);
  const [manualValues, setManualValues] = useState({
    calories: '', totalFat: '', saturatedFat: '', transFat: '',
    polyunsaturatedFat: '', monounsaturatedFat: '', cholesterol: '', sodium: '',
    totalCarbs: '', dietaryFiber: '', totalSugars: '', addedSugars: '',
    protein: '', vitaminD: '', calcium: '', iron: '', potassium: '',
  });

  // Settings form
  const [settings, setSettings] = useState({
    servingSizeG: '',
    servingSizeLabel: '',
    servingSizeLabelFr: '',
    servingsPerContainer: '',
    productWeightG: '',
    weightLossPct: '0',
  });

  // Canadian label export: 'bilingual' (one table, both languages) or 'unilingual'
  // (separate English and French tables). Null = not currently exporting Canada.
  const [canadaExportMode, setCanadaExportMode] = useState(null);

  // Per-nutrient overrides — { sodium: '520' }. Only listed nutrients are pinned; the rest
  // stay recipe-derived. Values are kept as strings while editing, cleaned on send.
  const [overrides, setOverrides] = useState({});

  const latestApproved = useMemo(() => versions.find(v => v.status === 'approved') || null, [versions]);

  // Detect if the current recipe differs from the approved version's recipe snapshot
  const recipeChanged = useMemo(() => {
    if (!latestApproved?.snapshot?.recipeSnapshot) return false;
    const approved = latestApproved.snapshot.recipeSnapshot;
    if (approved.length !== productIngredients.length) return true;
    const approvedMap = Object.fromEntries(approved.map(r => [String(r.ingredientId), Number(r.expectedQuantityKg)]));
    return productIngredients.some(pi => {
      const aqty = approvedMap[String(pi.ingredientId)];
      return aqty === undefined || Math.abs(aqty - Number(pi.expectedQuantityKg || 0)) > 0.001;
    });
  }, [latestApproved, productIngredients]);

  const load = useCallback(async () => {
    if (!product?.id) return;
    setLoading(true);
    try {
      const [p, c, v] = await Promise.all([
        getProductNutritionProfile(product.id),
        getIngredientCoverage(product.id),
        getNutritionVersions(product.id).catch(() => []),
      ]);
      setProfile(p);
      setCoverage(c);
      setVersions(v || []);
      if (p) {
        setSettings({
          servingSizeG:         p.servingSize        ?? '',
          servingSizeLabel:     p.servingSizeLabel   ?? '',
          servingSizeLabelFr:   p.servingSizeLabelFr ?? '',
          servingsPerContainer: p.servingsPerContainer ?? '',

          productWeightG:       p.productWeightG     ?? '',
          weightLossPct:        p.weightLossPct      ?? '0',
        });
        setOverrides(
          Object.fromEntries(Object.entries(p.manualOverrides || {}).map(([k, v]) => [k, String(v)]))
        );
        if (p.isManualOverride) {
          setManualMode(true);
          setManualValues({
            calories: p.calories ?? '', totalFat: p.totalFat ?? '',
            saturatedFat: p.saturatedFat ?? '', transFat: p.transFat ?? '',
            polyunsaturatedFat: p.polyunsaturatedFat ?? '', monounsaturatedFat: p.monounsaturatedFat ?? '',
            cholesterol: p.cholesterol ?? '', sodium: p.sodium ?? '',
            totalCarbs: p.totalCarbs ?? '', dietaryFiber: p.dietaryFiber ?? '',
            totalSugars: p.totalSugars ?? '', addedSugars: p.addedSugars ?? '',
            protein: p.protein ?? '', vitaminD: p.vitaminD ?? '',
            calcium: p.calcium ?? '', iron: p.iron ?? '', potassium: p.potassium ?? '',
          });
        }
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [product?.id]);

  useEffect(() => { load(); }, [load]);

  const loadReadiness = useCallback(async () => {
    if (!product?.id) return;
    setReadinessLoading(true);
    try {
      const r = await getNutritionReadiness(product.id);
      setReadiness(r);
    } catch { setReadiness(null); }
    setReadinessLoading(false);
  }, [product?.id]);

  // Load readiness when label tab becomes active
  useEffect(() => {
    if (activeTab === 'label') loadReadiness();
  }, [activeTab, loadReadiness]);

  // Returns the result so callers can chain directly without waiting for state updates
  const runCalculate = async () => {
    setError('');
    if (!settings.servingSizeG || !settings.productWeightG) {
      setError('Serving size (g) and product weight (g) are required to calculate.');
      return null;
    }
    try {
      const result = await calculateNutrition(product.id, {
        servingSizeG:   Number(settings.servingSizeG),
        productWeightG: Number(settings.productWeightG),
        weightLossPct:  Number(settings.weightLossPct || 0),
        // Send the pinned nutrients explicitly (even when empty) so clearing an override
        // in the editor takes effect immediately instead of falling back to the saved set.
        overrides:      cleanOverrides(overrides),
      });
      setCalcResult(result);
      return result;
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      return null;
    }
  };

  const saveResult = async (result) => {
    const source = result?.rounded || calcResult?.rounded || profile;
    if (!source) { setError('Nothing to save — calculate first.'); return; }
    try {
      const data = {
        ...source,
        servingSize:          Number(settings.servingSizeG),
        servingSizeLabel:     settings.servingSizeLabel || null,
        servingSizeLabelFr:   settings.servingSizeLabelFr || null,
        // Persist the unrounded figures too — the Canadian table must be rounded from
        // these, not from the FDA-rounded columns. See docs/NUTRITION_LABEL_MODULE.md.
        // These already have any per-nutrient overrides merged in by the calculator.
        rawValues:            (result ?? calcResult)?.raw ?? null,
        manualOverrides:      cleanOverrides(overrides),
        servingsPerContainer: (result ?? calcResult)?.servingsPerContainer
                                ?? (settings.servingsPerContainer ? Number(settings.servingsPerContainer) : null),
        productWeightG:       Number(settings.productWeightG) || null,
        weightLossPct:        Number(settings.weightLossPct)  || 0,
        isManualOverride:     false,
        calculatedAt:         new Date().toISOString(),
      };
      const saved = await saveProductNutritionProfile(product.id, data);
      setProfile(saved);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Used in Settings tab: calculate + immediately persist in one click
  const handleCalculateAndSave = async () => {
    setCalculating(true);
    setSaving(true);
    setError('');
    const result = await runCalculate();
    if (result) { await saveResult(result); loadReadiness(); }
    setCalculating(false);
    setSaving(false);
  };

  const handleSaveManual = async () => {
    setSaving(true);
    setError('');
    if (!settings.servingSizeG) {
      setError('Serving size (g) is required.');
      setSaving(false);
      return;
    }
    try {
      const manualNumeric = Object.fromEntries(
        Object.entries(manualValues).map(([k, v]) => [k, v !== '' ? Number(v) : null])
      );
      const data = {
        servingSize:          Number(settings.servingSizeG),
        servingSizeLabel:     settings.servingSizeLabel || null,
        servingSizeLabelFr:   settings.servingSizeLabelFr || null,
        servingsPerContainer: settings.servingsPerContainer ? Number(settings.servingsPerContainer) : null,
        productWeightG:       settings.productWeightG ? Number(settings.productWeightG) : null,
        weightLossPct:        0,
        isManualOverride:     true,
        calculatedAt:         new Date().toISOString(),
        // On a manual profile there is no separate unrounded source — the entered figures
        // are the asserted values, so they're what the Canadian rounding is applied to.
        rawValues:            manualNumeric,
        ...manualNumeric,
      };
      const saved = await saveProductNutritionProfile(product.id, data);
      setProfile(saved);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setSaving(false);
  };

  const handleApprove = async () => {
    setApproving(true);
    setError('');
    setReconciliationErrors([]);
    try {
      await approveNutritionVersion(product.id, approveNotes);
      setApproveNotes('');
      setApproveSuccess(true);
      await load();
      setTimeout(() => setApproveSuccess(false), 3000);
    } catch (e) {
      const resp = e.response?.data;
      if (resp?.reconciliationErrors?.length) {
        setReconciliationErrors(resp.reconciliationErrors);
        setError(resp.error || 'Reconciliation check failed.');
      } else {
        setError(resp?.error || e.message);
      }
    }
    setApproving(false);
  };

  const handlePrint = () => {
    setCanadaExportMode(null);
    setPrintReady(true);
    setTimeout(() => {
      window.print();
      setPrintReady(false);
    }, 300);
  };

  const handlePrintCanada = (mode) => {
    setCanadaExportMode(mode);
    setPrintReady(true);
    setTimeout(() => {
      window.print();
      setPrintReady(false);
      setCanadaExportMode(null);
    }, 300);
  };

  // Canadian table values, rounded server-side from the stored unrounded figures.
  // Falls back to a live recalculation's raw values when one is in memory, so a
  // just-calculated (not yet saved) profile can still be exported.
  const canadaData = useMemo(() => {
    if (calcResult?.canada) return calcResult.canada;
    return profile?.canada || null;
  }, [calcResult, profile]);

  const displayProfile = calcResult?.rounded
    ? { ...calcResult.rounded, servingSize: Number(settings.servingSizeG),
        servingSizeLabel: settings.servingSizeLabel,
        servingsPerContainer: calcResult.servingsPerContainer }
    : profile;

  if (loading) return <div className="text-muted small p-3">Loading nutrition data…</div>;

  return (
    <div>
      {/* Tab bar */}
      <ul className="nav nav-tabs mb-3" style={{ fontSize: '13px' }}>
        {[['label', 'Nutrition Label'], ['ingredients', 'Ingredient Nutrition'], ['settings', 'Serving & Weight']].map(
          ([key, label]) => (
            <li className="nav-item" key={key}>
              <button className={`nav-link py-1 px-3 ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}>
                {label}
                {key === 'ingredients' && coverage && (
                  <span className={`ms-1 badge ${coverage.coveragePct === 100 ? 'bg-success' : 'bg-warning text-dark'}`}
                    style={{ fontSize: '10px' }}>
                    {coverage.withNutrition}/{coverage.total}
                  </span>
                )}
              </button>
            </li>
          )
        )}
      </ul>

      {/* Label tab */}
      {activeTab === 'label' && (
        <div>
          {coverage && coverage.coveragePct < 100 && (
            <div className="alert alert-warning py-1 px-2 mb-3 small">
              {coverage.total - coverage.withNutrition} ingredient(s) are missing nutrition data.
              The label will only be an estimate.{' '}
              <button className="btn btn-link btn-sm p-0" onClick={() => setActiveTab('ingredients')}>
                Add data →
              </button>
            </div>
          )}

          {displayProfile && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <div className="form-check form-switch mb-0">
                <input className="form-check-input" type="checkbox" id="rawModeToggle"
                  checked={rawMode} onChange={e => setRawMode(e.target.checked)} />
                <label className="form-check-label small" htmlFor="rawModeToggle">
                  Raw values (unrounded) — for regulatory use only, not for label printing
                </label>
              </div>
            </div>
          )}

          {displayProfile ? (
            rawMode ? (
              <div style={{ maxWidth: '560px' }}>
                <div className="alert alert-info py-2 px-3 small mb-3">
                  Unrounded values with 2 decimal places. Use these for product registration (e.g. Chile RENAR). Do not use for FDA label printing.
                  {calcResult?.raw && <span className="ms-1 fw-semibold">Showing live calculated values.</span>}
                </div>
                <table className="table table-sm table-bordered" style={{ fontSize: '13px' }}>
                  <thead className="table-light">
                    <tr>
                      <th>Nutrient</th>
                      <th className="text-end">Per serving<br /><span className="fw-normal text-muted">({displayProfile.servingSize}g)</span></th>
                      <th className="text-end">Per 100g</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'calories',           label: 'Calories',             unit: 'kcal' },
                      { key: 'totalFat',           label: 'Total Fat',             unit: 'g'    },
                      { key: 'saturatedFat',       label: 'Saturated Fat',         unit: 'g'    },
                      { key: 'transFat',           label: 'Trans Fat',             unit: 'g'    },
                      { key: 'polyunsaturatedFat', label: 'Polyunsaturated Fat',   unit: 'g'    },
                      { key: 'monounsaturatedFat', label: 'Monounsaturated Fat',   unit: 'g'    },
                      { key: 'cholesterol',        label: 'Cholesterol',           unit: 'mg'   },
                      { key: 'sodium',             label: 'Sodium',                unit: 'mg'   },
                      { key: 'totalCarbs',         label: 'Total Carbohydrate',    unit: 'g'    },
                      { key: 'dietaryFiber',       label: 'Dietary Fiber',         unit: 'g'    },
                      { key: 'totalSugars',        label: 'Total Sugars',          unit: 'g'    },
                      { key: 'addedSugars',        label: 'Added Sugars',          unit: 'g'    },
                      { key: 'protein',            label: 'Protein',               unit: 'g'    },
                      { key: 'vitaminD',           label: 'Vitamin D',             unit: 'mcg'  },
                      { key: 'calcium',            label: 'Calcium',               unit: 'mg'   },
                      { key: 'iron',               label: 'Iron',                  unit: 'mg'   },
                      { key: 'potassium',          label: 'Potassium',             unit: 'mg'   },
                    ].map(({ key, label, unit }) => {
                      const val = calcResult?.raw?.[key] ?? profile?.[key];
                      const servingG = Number(displayProfile.servingSize) || 0;
                      const per100g = val != null && servingG > 0 ? Number(val) / servingG * 100 : null;
                      return (
                        <tr key={key}>
                          <td>{label}</td>
                          <td className="text-end font-monospace">
                            {val == null ? '—' : Number(val).toFixed(2)}
                          </td>
                          <td className="text-end font-monospace">
                            {per100g == null ? '—' : per100g.toFixed(2)}
                          </td>
                          <td className="text-muted">{unit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!calcResult?.raw && (
                  <div className="alert alert-warning py-2 px-3 mt-2 small d-flex align-items-center justify-content-between gap-2">
                    <span>Showing saved (rounded) values. Recalculate to see fully unrounded values.</span>
                    <button className="btn btn-sm btn-warning text-dark flex-shrink-0"
                      onClick={async (e) => {
                        const btn = e.currentTarget;
                        btn.disabled = true;
                        btn.textContent = 'Calculating…';
                        await runCalculate();
                        btn.disabled = false;
                        btn.textContent = 'Recalculate raw values';
                      }}>
                      Recalculate raw values
                    </button>
                  </div>
                )}
              </div>
            ) : (
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <NutritionLabel profile={displayProfile} />

              <div style={{ flex: 1, minWidth: '220px' }}>
                {/* Version status badge */}
                <div className="mb-3">
                  {latestApproved ? (
                    <span className="badge bg-success" style={{ fontSize: '11px' }}>
                      ✓ Approved — v{latestApproved.versionNumber}
                      {latestApproved.approvedAt && (
                        <> · {new Date(latestApproved.approvedAt).toLocaleDateString()}</>
                      )}
                    </span>
                  ) : (
                    <span className="badge bg-warning text-dark" style={{ fontSize: '11px' }}>
                      Draft — not yet approved
                    </span>
                  )}
                </div>

                {/* Recipe-changed warning */}
                {latestApproved && recipeChanged && (
                  <div className="alert alert-warning py-2 px-2 mb-3 small">
                    ⚠️ The recipe has changed since this label was approved.
                    Recalculate and approve a new version to keep the label current.
                    <br />
                    <button className="btn btn-link btn-sm p-0 mt-1"
                      onClick={() => setActiveTab('settings')}>
                      Recalculate →
                    </button>
                  </div>
                )}

                {/* Actions */}
                <div className="d-flex gap-2 flex-wrap mb-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={handlePrint}>
                    Export Label PDF <span className="text-muted">(US / FDA)</span>
                  </button>
                  <div className="btn-group">
                    <button className="btn btn-outline-secondary btn-sm"
                      onClick={() => handlePrintCanada('bilingual')}
                      disabled={!canadaData}
                      title={canadaData ? 'One table, English and French together'
                        : 'Recalculate and save the profile first — see note below'}>
                      🇨🇦 Export Canada PDF <span className="text-muted">(bilingual)</span>
                    </button>
                    <button className="btn btn-outline-secondary btn-sm"
                      onClick={() => handlePrintCanada('unilingual')}
                      disabled={!canadaData}
                      title={canadaData ? 'Separate English and French tables, side by side'
                        : 'Recalculate and save the profile first — see note below'}>
                      EN + FR separate
                    </button>
                  </div>
                  <NutritionCertificateButton productId={product.id} />
                </div>

                {/* The Canadian table must be rounded from unrounded values (B.01.401); a
                    profile saved before raw values were stored can't produce one correctly. */}
                {!canadaData && (
                  <div className="alert alert-warning py-2 px-2 mb-3" style={{ fontSize: '11px' }}>
                    <strong>Canadian export unavailable for this profile.</strong> Canada rounds
                    several nutrients differently than the FDA (potassium and calcium to the
                    nearest 25–50&nbsp;mg, iron to the nearest 0.25&nbsp;mg), so its table has to be
                    built from the unrounded values — rounding the stored FDA figures a second time
                    would print numbers that are off. Recalculate and save on the Settings tab to
                    store them, then approve a new version.
                  </div>
                )}
                {canadaData && !profile?.servingSizeLabelFr && (
                  <div className="alert alert-warning py-2 px-2 mb-3" style={{ fontSize: '11px' }}>
                    <strong>French serving size missing.</strong> The Canadian table needs a French
                    serving description (e.g. "1 tranche (55 g)") to be compliant. Add it on the
                    Settings tab — nutrient names are translated for you.
                  </div>
                )}

                {/* Approve & Lock section */}
                <div className="border rounded p-2 mb-3" style={{ fontSize: '12px' }}>
                  <div className="fw-semibold mb-2" style={{ fontSize: '12px' }}>
                    {latestApproved ? `Approve as v${(latestApproved.versionNumber || 0) + 1}` : 'Approve & Lock v1'}
                  </div>

                  {/* Readiness checklist */}
                  {(() => {
                    if (readinessLoading) return (
                      <div className="text-muted mb-2" style={{ fontSize: '11px' }}>Checking readiness…</div>
                    );
                    if (!readiness) return null;
                    const c = readiness.checks;
                    const items = [
                      {
                        pass: c.hasProfile.pass,
                        label: 'Nutrition profile calculated',
                        fix: () => setActiveTab('settings'),
                        fixLabel: 'Calculate →',
                      },
                      {
                        pass: c.ingredientCoverage.pass,
                        label: `Ingredients have nutrition data (${c.ingredientCoverage.withNutrition}/${c.ingredientCoverage.total})`,
                        fix: () => setActiveTab('ingredients'),
                        fixLabel: 'Add data →',
                      },
                      ...(c.fatProfileComplete.pass != null ? [{
                        pass: c.fatProfileComplete.pass,
                        label: c.fatProfileComplete.pass
                          ? 'Fat sub-profiles complete'
                          : `Fat sub-profiles missing: ${c.fatProfileComplete.issues.join(', ')}`,
                        fix: () => setActiveTab('ingredients'),
                        fixLabel: 'Fix →',
                      }] : []),
                      {
                        pass: c.ingredientSubFatConsistent.pass,
                        label: c.ingredientSubFatConsistent.pass
                          ? 'Fat values consistent per ingredient'
                          : `Sub-fat exceeds total fat in: ${c.ingredientSubFatConsistent.issues.map(s => s.split('"')[1]).join(', ')}`,
                        fix: () => setActiveTab('ingredients'),
                        fixLabel: 'Fix →',
                      },
                      ...(c.carbReconciliation.pass != null ? [{
                        pass: c.carbReconciliation.pass,
                        label: c.carbReconciliation.pass
                          ? `Carb reconciliation OK (fiber+sugars = ${c.carbReconciliation.subSum}g ≤ total carbs ${c.carbReconciliation.totalCarbs}g)`
                          : `Carb mismatch: fiber+sugars = ${c.carbReconciliation.subSum}g exceeds total carbs ${c.carbReconciliation.totalCarbs}g (delta ${c.carbReconciliation.delta}g)`,
                        fix: () => setActiveTab('ingredients'),
                        fixLabel: 'Fix →',
                      }] : []),
                    ];
                    return (
                      <div className="mb-2 p-2 rounded" style={{
                        background: readiness.ready ? '#f0fdf4' : '#fef9f0',
                        border: `1px solid ${readiness.ready ? '#bbf7d0' : '#fed7aa'}`,
                      }}>
                        <div className="fw-semibold mb-1" style={{ fontSize: '11px', color: readiness.ready ? '#166534' : '#92400e' }}>
                          {readiness.ready ? '✓ Ready to approve' : '⚠ Issues to fix before approving'}
                          <button className="btn btn-link p-0 ms-2" style={{ fontSize: '10px' }}
                            onClick={loadReadiness}>refresh</button>
                        </div>
                        {items.map((item, i) => (
                          <div key={i} className="d-flex align-items-baseline gap-1" style={{ fontSize: '11px', marginBottom: '2px' }}>
                            <span style={{ color: item.pass ? '#16a34a' : '#dc2626', fontWeight: 600, minWidth: 12 }}>
                              {item.pass ? '✓' : '✗'}
                            </span>
                            <span style={{ color: item.pass ? '#166534' : '#7f1d1d' }}>{item.label}</span>
                            {!item.pass && item.fix && (
                              <button className="btn btn-link p-0 ms-1" style={{ fontSize: '10px' }}
                                onClick={item.fix}>{item.fixLabel}</button>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  <textarea
                    className="form-control form-control-sm mb-2"
                    rows={2}
                    placeholder="Optional notes (e.g. initial release, updated flour source…)"
                    value={approveNotes}
                    onChange={e => setApproveNotes(e.target.value)}
                  />
                  {approveSuccess && (
                    <div className="text-success mb-1" style={{ fontSize: '11px' }}>
                      ✓ Label locked as v{latestApproved?.versionNumber}
                    </div>
                  )}
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleApprove}
                    disabled={approving || !profile || (readiness && !readiness.ready)}>
                    {approving ? 'Approving…' : '🔒 Approve & Lock'}
                  </button>
                  <div className="text-muted mt-1" style={{ fontSize: '10px' }}>
                    Locks the current calculated values. The recipe can still be edited — approval
                    is required again before the certificate reflects changes.
                  </div>
                </div>

                {/* Version history */}
                {versions.length > 0 && (
                  <div>
                    <button
                      className="btn btn-link btn-sm p-0 text-muted"
                      style={{ fontSize: '11px' }}
                      onClick={() => setShowVersionHistory(v => !v)}>
                      {showVersionHistory ? '▲ Hide' : '▼ Show'} version history ({versions.length})
                    </button>
                    {showVersionHistory && (
                      <table className="table table-sm mt-1" style={{ fontSize: '11px' }}>
                        <thead>
                          <tr>
                            <th>Ver.</th>
                            <th>Status</th>
                            <th>Approved</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {versions.map(v => (
                            <tr key={v.id}>
                              <td>v{v.versionNumber}</td>
                              <td>
                                <span className={`badge ${v.status === 'approved' ? 'bg-success' : 'bg-secondary'}`}
                                  style={{ fontSize: '9px' }}>
                                  {v.status}
                                </span>
                              </td>
                              <td>{v.approvedAt ? new Date(v.approvedAt).toLocaleDateString() : '—'}</td>
                              <td className="text-muted">{v.notes || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {profile?.calculatedAt && (
                  <div className="text-muted mt-2" style={{ fontSize: '11px' }}>
                    Last calculated: {new Date(profile.calculatedAt).toLocaleDateString()}
                    {' · '}
                    <button className="btn btn-link btn-sm p-0 small" onClick={() => setActiveTab('settings')}>
                      Update settings →
                    </button>
                  </div>
                )}
              </div>
            </div>
            )
          ) : (
            <div className="text-muted p-3 text-center">
              <div className="mb-2">No nutrition profile yet.</div>
              <button className="btn btn-outline-primary btn-sm" onClick={() => setActiveTab('settings')}>
                Set serving size to get started →
              </button>
            </div>
          )}

          {error && (
            <div className="alert alert-danger py-2 px-2 mt-3 small">
              <div className="fw-semibold mb-1">{error}</div>
              {reconciliationErrors.length > 0 && (
                <ul className="mb-0 ps-3" style={{ fontSize: '11px' }}>
                  {reconciliationErrors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* Ingredients nutrition tab */}
      {activeTab === 'ingredients' && (
        <div>
          <div className="small text-muted mb-3">
            Set per-100g nutrition for each ingredient. Data is imported from USDA FoodData Central
            or entered manually.
          </div>

          {/* Fat sub-profile gap banner */}
          {coverage?.incompleteFatProfiles?.length > 0 && (
            <div className="alert alert-warning py-2 px-3 mb-3" style={{ fontSize: '12px' }}>
              <div className="fw-semibold mb-1">
                ⚠ Fat sub-profile data missing ({coverage.incompleteFatProfiles.length} ingredient{coverage.incompleteFatProfiles.length !== 1 ? 's' : ''})
              </div>
              <div className="mb-1">
                {coverage.incompleteFatProfiles.map((ing, i) => (
                  <span key={ing.ingredientId}>
                    <strong>{ing.name}</strong>
                    <span className="text-muted ms-1">({ing.totalFatPer100g.toFixed(1)}g fat/100g)</span>
                    {i < coverage.incompleteFatProfiles.length - 1 && <span className="me-1">,</span>}
                  </span>
                ))}
              </div>
              <div className="text-muted" style={{ fontSize: '11px' }}>
                Look for the <span className="badge bg-warning text-dark" style={{ fontSize: '9px' }}>⚠ fat</span> badge
                on each row below. Open the row and use "Derive from USDA" to fill in sat/trans/poly/mono values.
              </div>
            </div>
          )}

          {productIngredients.length === 0 && (!coverage?.baseRecipeIngredients?.length) ? (
            <div className="text-muted small">This product has no recipe ingredients.</div>
          ) : (
            <>
              {productIngredients.length > 0 && (
                <>
                  {coverage?.baseRecipeIngredients?.length > 0 && (
                    <div className="text-muted small fw-semibold mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                      Direct Ingredients
                    </div>
                  )}
                  {productIngredients.map(ing => (
                    <IngredientNutritionRow
                      key={ing.ingredientId}
                      ingredient={ing}
                      onSaved={() => {
                        getIngredientCoverage(product.id).then(setCoverage).catch(() => {});
                        loadReadiness();
                      }}
                      readOnly={true}
                      onNavigateToIngredient={onNavigateToIngredient}
                    />
                  ))}
                </>
              )}
              {coverage?.baseRecipeIngredients?.length > 0 && (
                <>
                  <div className="text-muted small fw-semibold mt-3 mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11 }}>
                    Via Base Recipes
                  </div>
                  {coverage.baseRecipeIngredients.map((ing, idx) => (
                    <IngredientNutritionRow
                      key={`br-${ing.baseRecipeId}-${ing.ingredientId}-${idx}`}
                      ingredient={{ ingredientId: ing.ingredientId, name: ing.name, expectedQuantityKg: ing.effectiveKg }}
                      badge={<span className="badge text-bg-secondary ms-2" style={{ fontSize: 10, fontWeight: 400 }}>via {ing.baseRecipeName}</span>}
                      onSaved={() => {
                        getIngredientCoverage(product.id).then(setCoverage).catch(() => {});
                        loadReadiness();
                      }}
                      readOnly={true}
                      onNavigateToIngredient={onNavigateToIngredient}
                    />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Settings tab */}
      {activeTab === 'settings' && (
        <div style={{ maxWidth: '420px' }}>
          <div className="small text-muted mb-3">
            Enter the finished product weight and desired serving size. QuickLOG will calculate the
            nutrition facts from your recipe.
          </div>

          <div className="mb-3">
            <label className="form-label form-label-sm fw-semibold">
              Total Finished Weight per Unit (g) <span className="text-danger">*</span>
            </label>
            <input type="number" min="0" step="1" className="form-control form-control-sm"
              placeholder="e.g. 680 for a 1.5lb loaf"
              value={settings.productWeightG}
              onChange={e => setSettings(s => ({ ...s, productWeightG: e.target.value }))} />
            <div className="form-text">Weight of the product after baking, in grams.</div>
          </div>

          <RaccLookup
            onSelect={(r) => {
              setSettings(s => ({
                ...s,
                servingSizeG:     String(r.racc),
                servingSizeLabel: s.servingSizeLabel || `${r.note} (${r.racc}g)`,
              }));
            }}
          />

          <div className="mb-3">
            <label className="form-label form-label-sm fw-semibold">
              Serving Size (g) <span className="text-danger">*</span>
            </label>
            <input type="number" min="0" step="1" className="form-control form-control-sm"
              placeholder="e.g. 55"
              value={settings.servingSizeG}
              onChange={e => setSettings(s => ({ ...s, servingSizeG: e.target.value }))} />
            <div className="form-text">
              FDA recommended. Use the lookup above to find the RACC for your product category.
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label form-label-sm fw-semibold">
              Serving Size Label (optional)
            </label>
            <input type="text" className="form-control form-control-sm"
              placeholder='e.g. "1 slice (55g)"'
              value={settings.servingSizeLabel}
              onChange={e => setSettings(s => ({ ...s, servingSizeLabel: e.target.value }))} />
          </div>

          <div className="mb-3">
            <label className="form-label form-label-sm fw-semibold">
              Serving Size Label — French <span className="text-muted fw-normal">(Canadian label only)</span>
            </label>
            <input type="text" className="form-control form-control-sm"
              placeholder='e.g. "1 tranche (55 g)"'
              value={settings.servingSizeLabelFr}
              onChange={e => setSettings(s => ({ ...s, servingSizeLabelFr: e.target.value }))} />
            <div className="form-text">
              Required for a compliant Canadian Nutrition Facts table — the nutrient names are
              translated automatically, but the serving description has to be your wording.
            </div>
          </div>

          {/* Per-nutrient overrides. Distinct from full manual mode: pin only the values that
              have to match something external (printed cartons, an outside lab) and let the
              recipe drive the rest. */}
          <div className="mb-3">
            <label className="form-label form-label-sm fw-semibold mb-1">
              Pin individual nutrients <span className="text-muted fw-normal">(optional)</span>
            </label>
            <div className="form-text mt-0 mb-2">
              Leave blank to calculate from the recipe. Fill one in to pin just that nutrient —
              useful when cartons are already printed with a slightly different figure and you
              need the system to match. Pinned values feed both the US and Canadian labels.
            </div>
            <div className="table-responsive" style={{ maxHeight: '260px', overflowY: 'auto' }}>
              <table className="table table-sm mb-1" style={{ fontSize: '11px' }}>
                <thead className="table-light" style={{ position: 'sticky', top: 0 }}>
                  <tr>
                    <th>Nutrient</th>
                    <th style={{ width: '90px' }}>Calculated</th>
                    <th style={{ width: '110px' }}>Pinned value</th>
                    <th style={{ width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {OVERRIDABLE_NUTRIENTS.map(([key, label, unit]) => {
                    const calc = calcResult?.raw?.[key];
                    const pinned = overrides[key] ?? '';
                    return (
                      <tr key={key} className={pinned !== '' ? 'table-warning' : ''}>
                        <td>{label}</td>
                        <td className="text-muted">
                          {calc == null ? '—' : `${Math.round(Number(calc) * 100) / 100}${unit}`}
                        </td>
                        <td>
                          <input
                            type="number" min="0" step="any"
                            className="form-control form-control-sm"
                            style={{ fontSize: '11px' }}
                            placeholder="—"
                            value={pinned}
                            onChange={e => setOverrides(o => ({ ...o, [key]: e.target.value }))}
                          />
                        </td>
                        <td>
                          {pinned !== '' && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-danger"
                              title="Clear this pin and go back to the calculated value"
                              onClick={() => setOverrides(o => {
                                const next = { ...o };
                                delete next[key];
                                return next;
                              })}
                            >✕</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {Object.keys(cleanOverrides(overrides)).length > 0 && (
              <div className="alert alert-warning py-1 px-2 mb-0" style={{ fontSize: '11px' }}>
                {Object.keys(cleanOverrides(overrides)).length} nutrient(s) pinned — these will not
                change when the recipe changes. Recalculate and save to apply.
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label form-label-sm fw-semibold">
              Baking Weight Loss % (moisture loss)
            </label>
            <input type="number" min="0" max="50" step="0.1" className="form-control form-control-sm"
              placeholder="e.g. 12"
              value={settings.weightLossPct}
              onChange={e => setSettings(s => ({ ...s, weightLossPct: e.target.value }))} />
            <div className="form-text">
              Typical bread: 10–15%. This concentrates nutrients. Leave as 0 if unknown.
            </div>
          </div>

          {error && <div className="alert alert-danger py-1 px-2 mb-2 small">{error}</div>}

          {!manualMode && (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button className="btn btn-primary btn-sm"
                onClick={handleCalculateAndSave}
                disabled={calculating || saving}>
                {(calculating || saving) ? 'Saving…' : profile ? 'Recalculate & Save' : 'Calculate & Save'}
              </button>
              {profile && (
                <span className="text-muted small">
                  Last saved: {new Date(profile.updatedAt || profile.calculatedAt).toLocaleDateString()}
                  {' · '}
                  <button className="btn btn-link btn-sm p-0" onClick={() => setActiveTab('label')}>
                    View label →
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Manual override toggle */}
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
            <div className="form-check form-switch mb-2">
              <input className="form-check-input" type="checkbox" id="manualModeToggle"
                checked={manualMode}
                onChange={e => {
                  setManualMode(e.target.checked);
                  setError('');
                  if (e.target.checked && profile) {
                    setManualValues({
                      calories: profile.calories ?? '', totalFat: profile.totalFat ?? '',
                      saturatedFat: profile.saturatedFat ?? '', transFat: profile.transFat ?? '',
                      polyunsaturatedFat: profile.polyunsaturatedFat ?? '', monounsaturatedFat: profile.monounsaturatedFat ?? '',
                      cholesterol: profile.cholesterol ?? '', sodium: profile.sodium ?? '',
                      totalCarbs: profile.totalCarbs ?? '', dietaryFiber: profile.dietaryFiber ?? '',
                      totalSugars: profile.totalSugars ?? '', addedSugars: profile.addedSugars ?? '',
                      protein: profile.protein ?? '', vitaminD: profile.vitaminD ?? '',
                      calcium: profile.calcium ?? '', iron: profile.iron ?? '', potassium: profile.potassium ?? '',
                    });
                  }
                }} />
              <label className="form-check-label small" htmlFor="manualModeToggle">
                Manual entry (use spec sheet values)
              </label>
            </div>

            {manualMode && (
              <>
                <div className="text-muted small mb-2">
                  Enter per-serving values directly from a finished product spec sheet. Serving size fields above still apply.
                </div>
                <table className="table table-sm table-bordered mb-2" style={{ fontSize: '12px' }}>
                  <thead className="table-light">
                    <tr><th>Nutrient</th><th>Per serving</th><th>Unit</th></tr>
                  </thead>
                  <tbody>
                    {[
                      ['calories',           'Calories',            'kcal'],
                      ['totalFat',           'Total Fat',           'g'],
                      ['saturatedFat',       'Saturated Fat',       'g'],
                      ['transFat',           'Trans Fat',           'g'],
                      ['polyunsaturatedFat', 'Polyunsaturated Fat', 'g'],
                      ['monounsaturatedFat', 'Monounsaturated Fat', 'g'],
                      ['cholesterol',        'Cholesterol',         'mg'],
                      ['sodium',             'Sodium',              'mg'],
                      ['totalCarbs',         'Total Carbohydrate',  'g'],
                      ['dietaryFiber',       'Dietary Fiber',       'g'],
                      ['totalSugars',        'Total Sugars',        'g'],
                      ['addedSugars',        'Added Sugars',        'g'],
                      ['protein',            'Protein',             'g'],
                      ['vitaminD',           'Vitamin D',           'mcg'],
                      ['calcium',            'Calcium',             'mg'],
                      ['iron',               'Iron',                'mg'],
                      ['potassium',          'Potassium',           'mg'],
                    ].map(([key, label, unit]) => (
                      <tr key={key}>
                        <td>{label}</td>
                        <td>
                          <input type="number" min="0" step="any"
                            className="form-control form-control-sm"
                            style={{ fontSize: '11px' }}
                            value={manualValues[key]}
                            onChange={e => setManualValues(v => ({ ...v, [key]: e.target.value }))} />
                        </td>
                        <td className="text-muted">{unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {error && <div className="alert alert-danger py-1 px-2 mb-2 small">{error}</div>}
                <button className="btn btn-primary btn-sm" onClick={handleSaveManual} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Manual Values'}
                </button>
                {profile?.isManualOverride && (
                  <span className="text-muted small ms-2">
                    Last saved: {new Date(profile.updatedAt || profile.calculatedAt).toLocaleDateString()}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Print portal for PDF export */}
      {printReady && displayProfile && ReactDOM.createPortal(
        <>
          <style>{`
            @media print {
              #root { display: none !important; }
              #nutrition-print-portal { display: block !important; }
              @page { margin: 0.25in; size: ${canadaExportMode === 'unilingual' ? '8in 7in' : '3.5in 7in'}; }
              #nutrition-print-portal { page-break-inside: avoid; }
            }
          `}</style>
          <div id="nutrition-print-portal" style={{ display: 'none' }}>
            <div style={{ marginBottom: '6px', fontFamily: 'Arial', fontSize: '13px',
              fontWeight: 'bold', color: '#000' }}>
              {product?.name}
            </div>

            {canadaExportMode && canadaData ? (
              canadaExportMode === 'bilingual' ? (
                <CanadianNutritionLabel
                  values={canadaData.values}
                  dv={canadaData.dv}
                  servingEn={displayProfile.servingSizeLabel}
                  servingFr={profile?.servingSizeLabelFr || settings.servingSizeLabelFr}
                  servingsPerContainer={displayProfile.servingsPerContainer}
                  mode="bilingual"
                />
              ) : (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <CanadianNutritionLabel
                    values={canadaData.values} dv={canadaData.dv}
                    servingEn={displayProfile.servingSizeLabel}
                    servingsPerContainer={displayProfile.servingsPerContainer}
                    mode="en"
                  />
                  <CanadianNutritionLabel
                    values={canadaData.values} dv={canadaData.dv}
                    servingFr={profile?.servingSizeLabelFr || settings.servingSizeLabelFr}
                    servingsPerContainer={displayProfile.servingsPerContainer}
                    mode="fr"
                  />
                </div>
              )
            ) : (
              <NutritionLabel profile={displayProfile} />
            )}

            <div style={{ marginTop: '5px', fontFamily: 'Arial', fontSize: '9px', color: '#666' }}>
              {canadaExportMode
                ? 'Per serving. Rounded per Food and Drug Regulations B.01.401; % Daily Values per Health Canada Table of Daily Values (2022).'
                : 'Values per serving. Calculated from recipe data.'}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export { NutritionLabel, IngredientNutritionRow };
export default NutritionFactsPanel;
